import React, { useCallback, useState } from "react";
import { BackspaceIcon } from "@heroicons/react/24/solid";
import { FiPhone } from "react-icons/fi";

const KEY_ROWS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

const DialPad = ({
  onDial,
  disabled = false,
  calling = false,
  callLabel = "Call",
  callingLabel = "Connecting…",
  placeholder = "Enter number",
  showTitle = false,
  title = "Dialer",
}) => {
  const [number, setNumber] = useState("");

  const appendDigit = useCallback((digit) => {
    setNumber((current) => `${current}${digit}`);
  }, []);

  const backspace = useCallback(() => {
    setNumber((current) => current.slice(0, -1));
  }, []);

  const handleCall = useCallback(() => {
    const trimmed = number.trim();
    if (!trimmed) return;
    onDial(trimmed);
  }, [number, onDial]);

  const canCall = Boolean(number.trim()) && !disabled && !calling;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      {showTitle ? <div className="mb-2 text-sm font-semibold text-slate-700">{title}</div> : null}

      <div className="relative mb-3">
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-left text-base font-medium tracking-wide text-slate-900 outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
          placeholder={placeholder}
          value={number}
          onChange={(event) => setNumber(event.target.value.replace(/[^\d*#+]/g, ""))}
          disabled={disabled || calling}
          aria-label={placeholder}
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-40"
          onClick={backspace}
          disabled={disabled || calling || !number}
          aria-label="Backspace"
        >
          <BackspaceIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {KEY_ROWS.flat().map((key) => (
          <button
            key={key}
            type="button"
            className="flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-xl font-medium text-slate-700 transition hover:bg-slate-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled || calling}
            onClick={() => appendDigit(key)}
          >
            {key}
          </button>
        ))}
      </div>

      <button
        type="button"
        className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#52CDA1] text-sm font-semibold text-white transition hover:bg-[#44bf93] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
        disabled={!canCall}
        onClick={handleCall}
      >
        <FiPhone className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
        {calling ? callingLabel : callLabel}
      </button>
    </div>
  );
};

export default DialPad;
